import {
  ArrowDownIcon,
  ArrowRightIcon,
  Calendar01Icon,
  WarningIcon,
} from "vega-icons";

import { TeaserEmailStatus } from "@vega-api/generated/types/teaserEmailStatus";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { Checkbox } from "@vega-core/components/shadcn/checkbox";
import { Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { parseUtcDate } from "@vega-core/utils/formatDate";

import { DraftEmailForm } from "../DraftEmail/DraftEmailForm";
import { formatScheduledLabel } from "../DraftEmail/DraftStatusButton";

import { useSendDraftRowData } from "./useSendDraftRowData";
import { type SendDraftRowModel } from "./useSendDraftsManagement";

type SendDraftRowProps = {
  dealId: string;
  row: SendDraftRowModel;
  checked: boolean;
  expanded: boolean;
  disabled: boolean;
  readOnly: boolean;
  onToggleChecked: (checked: boolean) => void;
  onToggleExpanded: () => void;
  onSaved: () => void;
};

function SendDraftRowEditor({
  dealId,
  row,
  readOnly,
  onSaved,
  onCancel,
}: {
  dealId: string;
  row: SendDraftRowModel;
  readOnly: boolean;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { accessUsers, seedUserIds, initialValues, isLoading } =
    useSendDraftRowData({
      dealId,
      limitedPartnerId: row.limitedPartnerId,
      draft: row.draft,
    });

  if (isLoading) {
    return (
      <div
        className="flex min-h-32 items-center justify-center border-t border-border/primary"
        id={`send-draft-editor-${row.draft.id}`}
      >
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div
      className="border-t border-border/primary bg-background/subtle px-4 py-4"
      id={`send-draft-editor-${row.draft.id}`}
    >
      <DraftEmailForm
        accessUsers={accessUsers}
        autoSave={!readOnly}
        dealId={dealId}
        dealShortlistItemId={row.draft.dealShortlistItemId}
        emailId={row.draft.id}
        initialValues={initialValues}
        isScheduled={row.draft.status === TeaserEmailStatus.SCHEDULED}
        limitedPartnerId={row.limitedPartnerId}
        onCancel={onCancel}
        onSaved={onSaved}
        onScheduleCancelled={onSaved}
        readOnly={readOnly}
        seedUserIds={seedUserIds}
      />
    </div>
  );
}

export function SendDraftRow({
  dealId,
  row,
  checked,
  expanded,
  disabled,
  readOnly,
  onToggleChecked,
  onToggleExpanded,
  onSaved,
}: SendDraftRowProps) {
  const toCount = row.draft.recipients?.length ?? 0;
  const ccCount = row.draft.ccRecipients?.length ?? 0;

  return (
    <div
      className={cnTwMerge(
        "rounded-lg border border-border/primary transition-opacity",
        !checked && "opacity-60",
      )}
      data-testid={`send-draft-row-${row.draft.id}`}
    >
      <div className="flex items-start gap-3 p-4">
        <Checkbox
          aria-label={row.lpName}
          checked={checked}
          className="mt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-default focus-visible:ring-offset-1"
          disabled={disabled}
          onCheckedChange={(value) => onToggleChecked(value === true)}
        />
        <button
          aria-controls={`send-draft-editor-${row.draft.id}`}
          aria-expanded={expanded}
          className="focus-outline-themed flex flex-1 items-start justify-between gap-3 rounded text-left"
          onClick={onToggleExpanded}
          type="button"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="body-base-medium text-text/primary">
                {row.lpName}
              </span>
              {row.hasRecipients ? (
                <>
                  <Tag variant={TagVariants.Default}>
                    {m.coInvestments_sendDrafts_recipientsTo({
                      count: toCount,
                    })}
                  </Tag>
                  {ccCount > 0 ? (
                    <Tag variant={TagVariants.Regular}>
                      {m.coInvestments_sendDrafts_recipientsCc({
                        count: ccCount,
                      })}
                    </Tag>
                  ) : null}
                </>
              ) : (
                <Tag
                  dataTestId="send-draft-no-recipients"
                  variant={TagVariants.Warning}
                >
                  <span className="flex items-center gap-1">
                    <WarningIcon className="size-3" />
                    {m.coInvestments_sendDrafts_noRecipients()}
                  </span>
                </Tag>
              )}
              {row.draft.scheduledAt ? (
                <Tag variant={TagVariants.Pending}>
                  <span className="flex items-center gap-1">
                    <Calendar01Icon className="size-3" />
                    {m.coInvestments_sendDrafts_scheduledBadge({
                      dateTime: formatScheduledLabel(
                        parseUtcDate(row.draft.scheduledAt),
                      ),
                    })}
                  </span>
                </Tag>
              ) : null}
            </div>
            <p className="body-base-regular truncate text-text/secondary">
              {row.draft.subject}
            </p>
          </div>
          {expanded ? (
            <ArrowDownIcon className="mt-1 size-4 text-text/secondary" />
          ) : (
            <ArrowRightIcon className="mt-1 size-4 text-text/secondary" />
          )}
        </button>
      </div>

      {expanded ? (
        <SendDraftRowEditor
          dealId={dealId}
          onCancel={onToggleExpanded}
          onSaved={onSaved}
          readOnly={readOnly}
          row={row}
        />
      ) : null}
    </div>
  );
}
